import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@monksflow/monks-ui';
import {
  createMediaGuideline,
  editableVersion,
  GUIDELINE_KIND_LABELS,
  GUIDELINE_LEVEL_LABELS,
  GUIDELINE_TIER_LABELS,
  IMPLEMENTED_GUIDELINE_KINDS,
  MEDIA_GUIDELINE_QUERY_KEYS,
  platformPlanRulesQueryOptions,
  startGuidelineDraft,
  updateGuidelineDraft,
  type GuidelineKind,
  type GuidelineLevel,
  type GuidelineTier,
  type MediaGuideline,
} from '@/entities/media-guideline';
import { notificationService } from '@/lib/services/notification.service';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import { useTranslation } from 'react-i18next';

const LEVELS: GuidelineLevel[] = ['campaign', 'adGroup', 'ad', 'creative'];
const TIERS: GuidelineTier[] = ['block', 'warn', 'advise'];

export interface GuidelineEditorDialogProps {
  open: boolean;
  /** Absent creates a new guideline; present edits that guideline's draft. */
  guideline?: MediaGuideline;
  onClose: () => void;
}

/** One row of the naming-convention editor. Allowed values are typed comma-separated. */
export interface SegmentRow {
  label: string;
  allowed: string;
}

export interface FormState {
  name: string;
  description: string;
  platform: string;
  campaignCategory: string;
  kind: GuidelineKind;
  level: GuidelineLevel;
  tier: GuidelineTier;
  fieldKey: string;
  expected: string;
  min: string;
  max: string;
  unit: string;
  separator: string;
  segments: SegmentRow[];
  why: string;
  howToFix: string;
}

function initialState(guideline?: MediaGuideline): FormState {
  const version = guideline ? editableVersion(guideline) : undefined;
  const rule = (version?.rule ?? {}) as {
    fieldKey?: string;
    expected?: unknown;
    min?: number;
    max?: number;
    unit?: string;
    separator?: string;
    segments?: { label: string; oneOf?: string[] }[];
  };

  return {
    name: guideline?.name ?? '',
    description: guideline?.description ?? '',
    platform: guideline?.platform ?? '',
    campaignCategory: guideline?.campaignCategory ?? '',
    kind: guideline?.kind ?? 'required_value',
    level: version?.level ?? 'campaign',
    tier: version?.tier ?? 'warn',
    fieldKey: rule.fieldKey ?? '',
    expected: rule.expected === undefined ? '' : String(rule.expected),
    min: rule.min === undefined ? '' : String(rule.min),
    max: rule.max === undefined ? '' : String(rule.max),
    unit: rule.unit ?? '',
    separator: rule.separator ?? '_',
    segments: rule.segments?.length
      ? rule.segments.map((segment) => ({
          label: segment.label,
          allowed: segment.oneOf?.join(', ') ?? '',
        }))
      : [{ label: '', allowed: '' }],
    why: version?.why ?? '',
    howToFix: version?.howToFix ?? '',
  };
}

/**
 * The rule body for the chosen kind.
 *
 * Kept beside the form rather than inside the mutation so the save button can ask
 * the same question the backend will: is this rule actually sayable?
 */
export function ruleFor(form: FormState): Record<string, unknown> {
  const fieldKey = form.fieldKey.trim();

  if (form.kind === 'numeric_bound') {
    return {
      fieldKey,
      ...(form.min.trim() ? { min: Number(form.min) } : {}),
      ...(form.max.trim() ? { max: Number(form.max) } : {}),
      ...(form.unit.trim() ? { unit: form.unit.trim() } : {}),
    };
  }

  if (form.kind === 'naming_pattern') {
    return {
      fieldKey,
      separator: form.separator,
      segments: form.segments.map((segment) => {
        const oneOf = segment.allowed
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean);
        return { label: segment.label.trim(), ...(oneOf.length ? { oneOf } : {}) };
      }),
    };
  }

  return { fieldKey, expected: form.expected.trim() };
}

/** A bound end that was typed but is not a number the rule can hold. */
function isUnusableBound(raw: string): boolean {
  return raw.trim() !== '' && !Number.isFinite(Number(raw));
}

/** Whether the rule is complete enough to be worth sending. */
export function ruleIsComplete(form: FormState): boolean {
  if (!form.fieldKey.trim()) return false;
  if (form.kind === 'numeric_bound') {
    // `1,000` and `$500` are freely typeable here, and `Number()` turns both into
    // NaN, which JSON sends as null — indistinguishable from "no bound given", so
    // the backend accepts the guideline with the end silently dropped and the check
    // never fires. Refuse it here, where the rest of the gating already lives.
    if (isUnusableBound(form.min) || isUnusableBound(form.max)) return false;
    // A floor above its ceiling is refused by the backend, so it can only come back
    // as a 400 on save. Same class as the NaN bound above: the round trip is what
    // surfaces it, and the button is where it should have been caught.
    if (form.min.trim() && form.max.trim() && Number(form.min) > Number(form.max)) return false;
    return Boolean(form.min.trim() || form.max.trim());
  }
  if (form.kind === 'naming_pattern') {
    if (!form.separator) return false;
    // An allowed value carrying the separator could never match one position, which
    // `parseSegment` also refuses. Checked on the split values rather than the raw
    // text, since a comma-separated list legitimately contains commas.
    const separatorInAllowedValue = form.segments.some((segment) =>
      segment.allowed
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
        .some((value) => value.includes(form.separator)),
    );
    if (separatorInAllowedValue) return false;
    return form.segments.every((segment) => segment.label.trim());
  }
  return Boolean(form.expected.trim());
}

/**
 * Creates a guideline or edits its open draft.
 *
 * The kind list is `IMPLEMENTED_GUIDELINE_KINDS` rather than every kind declared:
 * a stored rule the backend has no evaluator for reads as a check that is running
 * when it is not, so a kind becomes offerable here only once one exists.
 *
 * Editing a guideline whose current version is published opens a NEW draft first
 * rather than changing it in place, so every job already checked against that
 * version keeps its verdict.
 */
/**
 * Stands in for "no platform", which the API expresses as the field being absent.
 * A select cannot hold an empty string as a value, so the two are mapped at the edge.
 */
// eslint-disable-next-line i18next/no-literal-string -- sentinel select value, never rendered
const EVERY_PLATFORM = '__every';

export function GuidelineEditorDialog({ open, guideline, onClose }: GuidelineEditorDialogProps) {
  const { t } = useTranslation('knowledge');
  const queryClient = useQueryClient();
  // The same list the standard rules are published under, which is the point: a
  // guideline can only be scoped to a platform pre-flight actually runs for.
  const { data: platforms } = useQuery(platformPlanRulesQueryOptions());

  const [form, setForm] = useState<FormState>(() => initialState(guideline));

  // Identity is not versioned, so a rename would land on the live guideline before the
  // draft carrying it is published. The API refuses one; this stops it being offered.
  const nameIsFixed = Boolean(guideline?.currentVersionId);

  // A guideline written before this field was a select, or scoped to a platform since
  // dropped from the list, keeps a value with no option to match. Select renders that
  // as an empty trigger, so the platform an existing rule is scoped to would read as
  // "every platform" on the one screen that shows it.
  const platformOptions = (platforms ?? []).map((each) => ({
    value: each.platform,
    label: each.label,
  }));
  if (form.platform && !platformOptions.some((each) => each.value === form.platform)) {
    platformOptions.push({ value: form.platform, label: form.platform });
  }

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((previous) => ({ ...previous, [key]: value }));

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        level: form.level,
        tier: form.tier,
        rule: ruleFor(form),
        why: form.why.trim() || undefined,
        howToFix: form.howToFix.trim() || undefined,
      };

      if (!guideline) {
        return createMediaGuideline({
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          kind: form.kind,
          platform: form.platform.trim() || undefined,
          campaignCategory: form.campaignCategory.trim() || undefined,
          ...body,
        });
      }

      const version = editableVersion(guideline);
      const draft =
        version?.status === 'draft' ? guideline : await startGuidelineDraft(guideline.id);
      const draftVersion = editableVersion(draft);

      return updateGuidelineDraft(guideline.id, {
        expectedRevision: draftVersion?.revision ?? 1,
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        ...body,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: MEDIA_GUIDELINE_QUERY_KEYS.all });
      notificationService.showSuccess(
        guideline
          ? t('mediaGuidelines.guidelineEditorDialog.draftSaved')
          : t('mediaGuidelines.guidelineEditorDialog.guidelineCreatedAsADraft'),
      );
      onClose();
    },
    onError: (error) => {
      const { detail } = parseProblemDetail(
        error,
        t('mediaGuidelines.guidelineEditorDialog.couldNotSaveTheGuideline'),
      );
      notificationService.showError(detail);
    },
  });

  const canSave = form.name.trim().length > 0 && ruleIsComplete(form) && !save.isPending;

  const setSegment = (index: number, key: keyof SegmentRow, value: string) =>
    setForm((previous) => ({
      ...previous,
      segments: previous.segments.map((segment, position) =>
        position === index ? { ...segment, [key]: value } : segment,
      ),
    }));

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !save.isPending && onClose()}>
      <DialogContent className="max-w-160 gap-0 p-4" data-testid="guideline-editor-dialog">
        <DialogHeader>
          <DialogTitle>
            {guideline
              ? t('mediaGuidelines.guidelineEditorDialog.editGuideline')
              : t('mediaGuidelines.guidelineEditorDialog.newGuideline')}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-4 pt-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="guideline-name">
              {t('mediaGuidelines.guidelineEditorDialog.name')}
            </Label>
            <Input
              id="guideline-name"
              value={form.name}
              placeholder={t('mediaGuidelines.guidelineEditorDialog.namePlaceholder')}
              onChange={(e) => set('name', e.target.value)}
              disabled={nameIsFixed}
              aria-describedby={nameIsFixed ? 'guideline-name-fixed' : undefined}
            />
            {/* Disabled rather than left to fail on save: saving a published guideline opens
                a fresh draft before it sends the rename, so a refusal would strand an empty
                draft the author never asked for. */}
            {nameIsFixed && (
              <p id="guideline-name-fixed" className="text-xs text-muted-foreground">
                {t('mediaGuidelines.guidelineEditorDialog.nameIsFixedOncePublished')}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="guideline-level">
                {t('mediaGuidelines.guidelineEditorDialog.appliesAt')}
              </Label>
              <Select
                value={form.level}
                onValueChange={(value) => set('level', value as GuidelineLevel)}
              >
                <SelectTrigger id="guideline-level">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEVELS.map((level) => (
                    <SelectItem key={level} value={level}>
                      {GUIDELINE_LEVEL_LABELS[level]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="guideline-tier">
                {t('mediaGuidelines.guidelineEditorDialog.tier')}
              </Label>
              <Select
                value={form.tier}
                onValueChange={(value) => set('tier', value as GuidelineTier)}
              >
                <SelectTrigger id="guideline-tier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIERS.map((tier) => (
                    <SelectItem key={tier} value={tier}>
                      {GUIDELINE_TIER_LABELS[tier]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="guideline-kind">
                {t('mediaGuidelines.guidelineEditorDialog.checksThat')}
              </Label>
              <Select
                value={form.kind}
                onValueChange={(value) => set('kind', value as GuidelineKind)}
                disabled={Boolean(guideline)}
              >
                <SelectTrigger id="guideline-kind" data-testid="guideline-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IMPLEMENTED_GUIDELINE_KINDS.map((kind) => (
                    <SelectItem key={kind} value={kind}>
                      {GUIDELINE_KIND_LABELS[kind]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="guideline-field">
                {t('mediaGuidelines.guidelineEditorDialog.field')}
              </Label>
              <Input
                id="guideline-field"
                value={form.fieldKey}
                // eslint-disable-next-line i18next/no-literal-string -- example platform field keys
                placeholder={form.kind === 'naming_pattern' ? 'name' : 'budgetType'}
                onChange={(e) => set('fieldKey', e.target.value)}
              />
            </div>
          </div>

          {form.kind === 'required_value' && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="guideline-expected">
                {t('mediaGuidelines.guidelineEditorDialog.mustEqual')}
              </Label>
              <Input
                id="guideline-expected"
                value={form.expected}
                // eslint-disable-next-line i18next/no-literal-string -- example platform field value
                placeholder="lifetime"
                onChange={(e) => set('expected', e.target.value)}
              />
            </div>
          )}

          {form.kind === 'numeric_bound' && (
            <div className="grid grid-cols-3 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="guideline-min">
                  {t('mediaGuidelines.guidelineEditorDialog.atLeast')}
                </Label>
                <Input
                  id="guideline-min"
                  value={form.min}
                  inputMode="decimal"
                  placeholder="500"
                  onChange={(e) => set('min', e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="guideline-max">
                  {t('mediaGuidelines.guidelineEditorDialog.atMost')}
                </Label>
                <Input
                  id="guideline-max"
                  value={form.max}
                  inputMode="decimal"
                  placeholder="50000"
                  onChange={(e) => set('max', e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="guideline-unit">
                  {t('mediaGuidelines.guidelineEditorDialog.unit')}
                </Label>
                <Input
                  id="guideline-unit"
                  value={form.unit}
                  placeholder="AUD"
                  onChange={(e) => set('unit', e.target.value)}
                />
              </div>
            </div>
          )}

          {form.kind === 'naming_pattern' && (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="guideline-separator">
                  {t('mediaGuidelines.guidelineEditorDialog.separator')}
                </Label>
                <Input
                  id="guideline-separator"
                  value={form.separator}
                  className="w-24"
                  placeholder="_"
                  onChange={(e) => set('separator', e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label>{t('mediaGuidelines.guidelineEditorDialog.partsInOrder')}</Label>
                {form.segments.map((segment, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      aria-label={t('mediaGuidelines.guidelineEditorDialog.partName', {
                        position: index + 1,
                      })}
                      value={segment.label}
                      placeholder={t('mediaGuidelines.guidelineEditorDialog.partNamePlaceholder')}
                      onChange={(e) => setSegment(index, 'label', e.target.value)}
                    />
                    <Input
                      aria-label={t('mediaGuidelines.guidelineEditorDialog.partAllowedValues', {
                        position: index + 1,
                      })}
                      value={segment.allowed}
                      placeholder={t(
                        'mediaGuidelines.guidelineEditorDialog.partAllowedPlaceholder',
                      )}
                      onChange={(e) => setSegment(index, 'allowed', e.target.value)}
                    />
                    <Button
                      variant="secondary"
                      aria-label={t('mediaGuidelines.guidelineEditorDialog.removePart', {
                        position: index + 1,
                      })}
                      disabled={form.segments.length === 1}
                      onClick={() =>
                        setForm((previous) => ({
                          ...previous,
                          segments: previous.segments.filter((_, position) => position !== index),
                        }))
                      }
                    >
                      {t('mediaGuidelines.guidelineEditorDialog.remove')}
                    </Button>
                  </div>
                ))}
                <Button
                  variant="secondary"
                  className="self-start"
                  onClick={() =>
                    setForm((previous) => ({
                      ...previous,
                      segments: [...previous.segments, { label: '', allowed: '' }],
                    }))
                  }
                >
                  {t('mediaGuidelines.guidelineEditorDialog.addPart')}
                </Button>
                <p className="text-muted-foreground text-sm">
                  {t('mediaGuidelines.guidelineEditorDialog.aNameIsCheckedPartByPart')}
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="guideline-platform">
                {t('mediaGuidelines.guidelineEditorDialog.platform')}
              </Label>
              {/* Chosen, not typed. A guideline is resolved by matching this against the
                  platform a plan publishes to, so a free-text field turned one typo into
                  a standard that silently never runs — and read as enforced from every
                  surface that lists it. The options are the platforms Delivery can
                  actually check, since a guideline scoped to any other would never fire
                  however it was spelled. */}
              <Select
                value={form.platform || EVERY_PLATFORM}
                onValueChange={(next) => set('platform', next === EVERY_PLATFORM ? '' : next)}
                disabled={Boolean(guideline)}
              >
                <SelectTrigger id="guideline-platform" data-testid="guideline-platform">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EVERY_PLATFORM}>
                    {t('mediaGuidelines.guidelineEditorDialog.everyPlatform')}
                  </SelectItem>
                  {platformOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="guideline-category">
                {t('mediaGuidelines.guidelineEditorDialog.campaignCategory')}
              </Label>
              <Input
                id="guideline-category"
                value={form.campaignCategory}
                placeholder={t('mediaGuidelines.guidelineEditorDialog.leaveBlankForEveryCategory')}
                onChange={(e) => set('campaignCategory', e.target.value)}
                disabled={Boolean(guideline)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="guideline-why">
              {t('mediaGuidelines.guidelineEditorDialog.whyThisExists')}
            </Label>
            <Textarea
              id="guideline-why"
              value={form.why}
              rows={2}
              placeholder={t('mediaGuidelines.guidelineEditorDialog.whyPlaceholder')}
              onChange={(e) => set('why', e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="guideline-fix">
              {t('mediaGuidelines.guidelineEditorDialog.howToFixIt')}
            </Label>
            <Textarea
              id="guideline-fix"
              value={form.howToFix}
              rows={2}
              placeholder={t('mediaGuidelines.guidelineEditorDialog.howToFixPlaceholder')}
              onChange={(e) => set('howToFix', e.target.value)}
            />
          </div>
        </DialogBody>

        <DialogFooter className="pt-4">
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            {t('mediaGuidelines.guidelineEditorDialog.cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={!canSave}>
            {guideline
              ? t('mediaGuidelines.guidelineEditorDialog.saveDraft')
              : t('mediaGuidelines.guidelineEditorDialog.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
